import { useContext } from 'react';
import { AppContext } from '@/context/AppContext';
import { affiliate_checkIsAllowToBookService } from '@/_helper/checkAccess';
import { PreloadedIconKey } from '@/_config/icons';

interface NavigationEntry {
    label: string;
    name: string;
    icon: PreloadedIconKey;
    index: number;
    alertCount?: number;
    iconAfter?: PreloadedIconKey;
}

const navigationEntries: Record<string, NavigationEntry> = {
    bookingDetails: {
        label: 'Buchungsübersicht',
        name: '',
        icon: 'explore',
        index: 0,
    },
    manageBooking: {
        label: 'Buchung verwalten oder ändern',
        name: 'buchung-verwalten',
        icon: 'editNote',
        index: 1,
    },
    documents: { label: 'Dokumente anzeigen', name: 'dokumente-anzeigen', icon: 'description', index: 2 },
    extraServices: { label: 'Zusatzleistungen buchen', name: 'zusatzleistung', icon: 'attractions', index: 3 },
    payment: {
        label: 'Zahlungsstatus einsehen',
        name: 'zahlungsstatus',
        icon: 'euro',
        index: 4,
    },
    cancellation: {
        label: 'Stornierungsoptionen',
        name: 'stornierungsoptionen',
        icon: 'freeCancellation',
        index: 5,
    },
    claim: {
        label: 'Reise reklamieren',
        name: 'reise-reklamieren',
        icon: 'syncProblem',
        index: 6,
    },
    onSpot: {
        label: 'Hilfe vor Ort',
        name: 'reise-hilfe-vor-ort',
        icon: 'help',
        iconAfter: 'errorFilled',
        index: 7,
    },
};

const useNavigationList = (alert?: {
    alertCount: {
        manageBooking: number | false;
        payment: number | false;
    };
    alertTotalCount: number;
}) => {
    const { isAffiliate, bookingData, travelType, isDebugMode, bookingState } = useContext(AppContext);
    if (!bookingData) {
        return null;
    }

    const { allowOnSpot, allowDigitalTourGuide, allowedPaymentMethods } = bookingData.data;
    const navigationList = [];

    if (!isDebugMode) {
        if (isAffiliate) {
            if (
                bookingState === 'OK' &&
                affiliate_checkIsAllowToBookService('extraServices').includes('extraServices')
            ) {
                navigationList.push(navigationEntries['extraServices']);
            }

            if (bookingState === 'over') {
                navigationList.push({ ...navigationEntries['claim'], index: 0 });
            }
        } else if (bookingState) {
            if (window.$browserInfo.device !== 'mobile') {
                navigationList.push(navigationEntries['bookingDetails']);
            }
            if (bookingState === 'over') {
                if (!allowedPaymentMethods.ch24PayAllowed) {
                    navigationList.push({
                        ...navigationEntries['payment'],
                        alertCount: alert?.alertCount.payment ?? 0,
                    });
                }
                if (travelType !== 'cruise') {
                    navigationList.push(navigationEntries['claim']);
                }
            } else if (bookingState === 'OK') {
                navigationList.push(
                    { ...navigationEntries['manageBooking'], alertCount: alert?.alertCount.manageBooking ?? 0 },
                    navigationEntries['documents'],
                    navigationEntries['extraServices']
                );

                if (!allowedPaymentMethods.ch24PayAllowed) {
                    navigationList.push({
                        ...navigationEntries['payment'],
                        alertCount: alert?.alertCount.payment ?? 0,
                    });
                }

                if (allowOnSpot || allowDigitalTourGuide) {
                    navigationList.push(navigationEntries['onSpot']);
                }

                if (bookingData?.data.allowedInteractions.cancellation) {
                    const { completeCancellation, partialCancellation } =
                        bookingData.data.allowedInteractions.cancellation;

                    // show cancellation tab if logged in and at least one of the cancellation options is available
                    if (completeCancellation || Object.values(partialCancellation).some((value) => value === true)) {
                        navigationList.push(navigationEntries['cancellation']);
                    }
                }
                if (travelType !== 'cruise') {
                    navigationList.push(navigationEntries['claim']);
                }
            } else if (bookingState === 'XX' && !allowedPaymentMethods.ch24PayAllowed) {
                navigationList.push({ ...navigationEntries['payment'], alertCount: alert?.alertCount.payment ?? 0 });
            }
        }
    } else {
        navigationList.push(navigationEntries['bookingDetails']);
        navigationList.push(navigationEntries['manageBooking']);
        navigationList.push(navigationEntries['documents']);
        navigationList.push(navigationEntries['extraServices']);
        navigationList.push({ ...navigationEntries['payment'], alertCount: alert?.alertCount.payment ?? 0 });
        navigationList.push(navigationEntries['cancellation']);
        navigationList.push(navigationEntries['claim']);
        navigationList.push(navigationEntries['onSpot']);
    }

    return navigationList;
};
export default useNavigationList;
